<template>
    <TDataTable :value="sortedExpenses" class="invoices-table" data-testid="invoices-list-table" scrollable>
        <TColumn
            v-for="column in ['name', 'status', 'date', 'total', 'action'] as InvoiceTableColumn[]"
            :key="column"
            :field="column"
            class="invoices-table__column large-size"
            :class="`${column}-column`"
            :frozen="column === 'action'"
            align-frozen="right"
        >
            <template #header>
                <div class="header / flex" @click="handleSort(column)">
                    <span>{{ t(`projectclient.projectview.invoices.table.column.${column}`) }}</span>
                    <VJoyIcon v-if="sortBy === column" :name="getSortIcon(column)" size="xsmall" />
                </div>
            </template>
            <template #body="{data}">
                <TableStatusColumn v-if="column === 'status'" :row-id="data.id" :status="data.status" />
                <div v-else-if="column === 'action'">
                    <NuxtLink v-if="data.pdfUrl" :to="data.pdfUrl" target="_blank" rel="noopener" external>
                        {{ t('projectclient.projectview.invoices.table.column.action.VIEW') }}
                    </NuxtLink>
                    <span v-else>{{ '-' }}</span>
                </div>
                <div v-else-if="column === 'name'" class="flex flex-col">
                    <div>{{ t(`projectclient.projectview.invoices.table.column.type.${data.type}`) }}</div>
                    <div class="name-label">{{ data.name }}</div>
                </div>
                <div v-else>{{ data[column] }}</div>
            </template>
        </TColumn>
    </TDataTable>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {type FormattedInvoiceTableItem, type InvoiceTableColumn, type ColumnSortOrder} from '@features/project-view/types';
    import {useTranslation} from '#imports';
    import {createDataTableComponents} from '@malt/joy-entrypoint/src/vue/utils';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import TableStatusColumn from './TableStatusColumn.vue';

    const {t} = useTranslation();

    // table structure
    const {TDataTable, TColumn} = createDataTableComponents<FormattedInvoiceTableItem>();

    const props = defineProps<{
        tableData: FormattedInvoiceTableItem[];
    }>();

    // sorting functionality
    const sortBy = ref<InvoiceTableColumn>('date');
    const sortOrder = ref<ColumnSortOrder>('desc');

    function handleSort(column: InvoiceTableColumn) {
        if (column === 'action') return;
        if (column === sortBy.value) {
            sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
        } else {
            sortBy.value = column;
            sortOrder.value = 'asc';
        }
    }

    const sortedExpenses = computed(() => {
        return [...props.tableData].sort((a, b) => {
            if (sortBy.value === 'name') {
                // compare type by hierarchy + name alphabetically
                const statusOrder = ['PROJECT_INVOICE', 'PROJECT_LOT_INVOICE', 'EXPENSE_INVOICE'];
                const indexA = statusOrder.indexOf(a.type);
                const indexB = statusOrder.indexOf(b.type);

                if (indexA !== indexB) {
                    return sortOrder.value === 'asc' ? indexA - indexB : indexB - indexA;
                }
                return a.name.localeCompare(b.name) * (sortOrder.value === 'asc' ? 1 : -1);
            } else if (sortBy.value === 'status') {
                // compare status by hierarchy
                const statusOrder = ['OVERDUE', 'PENDING', 'DONE'];
                const indexA = statusOrder.indexOf(a.status);
                const indexB = statusOrder.indexOf(b.status);

                if (sortOrder.value === 'asc') {
                    return indexA - indexB;
                } else {
                    return indexB - indexA;
                }
            } else if (sortBy.value === 'date' && a.rawDate && b.rawDate) {
                // compare raw values for dates
                const dateA = a.rawDate;
                const dateB = b.rawDate;

                if (sortOrder.value === 'asc') {
                    return dateA.getTime() - dateB.getTime();
                } else {
                    return dateB.getTime() - dateA.getTime();
                }
            } else if (sortBy.value === 'total') {
                // compare numbers for total
                const totalA = a.rawTotal;
                const totalB = b.rawTotal;

                if (sortOrder.value === 'asc') {
                    return totalA - totalB;
                } else {
                    return totalB - totalA;
                }
            } else {
                return a[sortBy.value].localeCompare(b[sortBy.value]) * (sortOrder.value === 'asc' ? 1 : -1);
            }
        });
    });

    const getSortIcon = (column: InvoiceTableColumn) => {
        if (sortBy.value === column && sortOrder.value === 'asc') {
            return 'sort-asc';
        } else {
            return 'sort-desc';
        }
    };
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text';

    .invoices-table {
        overflow: auto;
        border: 1px solid var(--joy-color-neutral-30) !important;
        border-radius: var(--joy-core-radius-3) !important;

        .header {
            flex-wrap: nowrap;

            @include text.getFontBySize(primary-350);
        }

        :deep(th.invoices-table__column) {
            &:hover:not(.action-column) {
                cursor: pointer;
            }
        }

        :deep(td.invoices-table__column) {
            &.action-column {
                color: var(--joy-color-secondary-30);
            }
        }

        // right align date + total column headers
        :deep(th.invoices-table__column) {
            &.date-column,
            &.total-column {
                & > .p-datatable-column-header-content {
                    justify-content: flex-end;
                }
            }
        }

        :deep(th.invoices-table__column),
        :deep(td.invoices-table__column) {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            padding: 0 var(--joy-core-spacing-3);

            &.total-column {
                @include text.getFontBySize(primary-350);

                color: var(--joy-color-gray-70);
            }

            &.name-column {
                .name-label {
                    @include text.getFontBySize(primary-350);

                    color: var(--joy-color-gray-70);
                }
            }

            &.total-column,
            &.date-column {
                text-align: right;
            }

            &.action-column {
                padding: 0 var(--joy-core-spacing-6);
                box-shadow:
                    0 0 0 0 var(--joy-color-neutral-0),
                    0 0 0 0 var(--joy-color-neutral-0),
                    0 0 0 0 var(--joy-color-neutral-30),
                    -3px 0 7px 0 var(--joy-color-neutral-30);
            }

            &.large-size {
                height: 56px;
            }
        }
    }
</style>
